/**
 * Gap Analysis > Conduct Assessment tool-test evidence: plain-language refusal text and the
 * completion rule, mirroring the API (it stays the source of truth; this only explains it).
 * Workflow slide 8 (technical assessment); ISO/IEC 27001:2022 A.5.36; Commandments IV, V, VII.
 */
import { ApiError, userMessageForError } from './apiError';
import { formatDdMmYyyyTime } from './formatDdMmYyyy';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

export const ASSESSMENT_COMPLETE_TEXT = 'The assessment is marked complete; reopen it first.';
export const TECH_EVAL_NEEDS_COMPLETE_TEXT = 'Mark the technical assessment complete (Conduct Assessment) before running the technical AI evaluation.';
export const REOPEN_BLOCKED_SHARED_TEXT = 'The technical scores have already been shared with the Lead Advisor, so the assessment cannot be reopened here. The Company SPOC sending the gap analysis back is the sanctioned way to reopen it.';
export const STALE_EVALUATION_TEXT = 'The evidence changed after the technical evaluation ran. Run the technical AI evaluation again on the current evidence, then approve it.';
export const EVIDENCE_CHANGED_TEXT = 'The approved tool-test evidence changed while the assessment was being completed. The latest evidence has been reloaded; review it and complete the assessment again.';
export const SPOC_TOOL_TESTS_NOT_PRESENTED_TEXT = 'Tool test summaries become available once the Lead Advisor presents the report';
export const LEGACY_COMPLETED_TEXT = 'Marked complete before evidence enforcement; no server-verified evidence';
export const REOPENED_WITH_EVALUATION_TEXT = 'Reopened. Technical evaluation must be run and approved again; the previous evaluation is kept in history';
export const REOPENED_TEXT = 'Reopened. Complete the assessment again once the evidence is updated.';
export const REOPEN_REASON_REQUIRED_TEXT = 'Reopening archives the technical evaluation you already validated. Say why you are reopening it so the archive records it.';
export const REOPEN_REASON_MIN_LENGTH = 5;
export const REOPEN_REASON_MAX_LENGTH = 1000;
export const TECH_HISTORY_TAMPERED_TEXT = 'The archive of previous evaluations failed its integrity check; contact the platform team';
export const GAP_LOCKED_STATUSES = ['Under Review', 'Approved by SPOC', 'Finalized'];

/** Gap analysis is locked once it is Under Review or later (the API refuses evidence and technical-evaluation writes). */
export function isGapAnalysisLocked(status) {
    return GAP_LOCKED_STATUSES.includes(status);
}

/** Why Reopen is unavailable, or '' when it is allowed (validated scores no longer block it; the server archives them). */
export function reopenBlockedReason({ sharedWithLA, gapLocked }) {
    if (sharedWithLA) return REOPEN_BLOCKED_SHARED_TEXT;
    if (gapLocked) return gapEvidenceReasonText('GAP_ANALYSIS_LOCKED');
    return '';
}

/** Validated scores are archived by a reopen, so the server then requires a human reason (REOPEN_REASON_REQUIRED). */
export function reopenReasonRequired({ techScoresValidated }) {
    return techScoresValidated === true;
}

/** Trimmed reason to send as the top-level `reopenReason`, or null when there is nothing to send. Capped to the server's 1000 characters. */
export function normalisedReopenReason(raw) {
    const t = typeof raw === 'string' ? raw.trim().slice(0, REOPEN_REASON_MAX_LENGTH) : '';
    return t || null;
}

/** Why evidence writes (generate / approve / reject / link / add or remove tools) are unavailable, or ''. */
export function evidenceWriteBlockedReason({ techAssessmentDone, gapLocked }) {
    if (gapLocked) return gapEvidenceReasonText('GAP_ANALYSIS_LOCKED');
    if (techAssessmentDone) return ASSESSMENT_COMPLETE_TEXT;
    return '';
}

/** Why Run / Approve technical AI evaluation is unavailable on the client's own knowledge, or ''. */
export function techEvalBlockedReason({ techAssessmentDone, gapLocked }) {
    if (gapLocked) return gapEvidenceReasonText('GAP_ANALYSIS_LOCKED');
    if (!techAssessmentDone) return TECH_EVAL_NEEDS_COMPLETE_TEXT;
    return '';
}

export const GAP_PUSH_ALLOWED_STATUSES = ['Approved by SPOC', 'Finalized'];
export const PUSH_PREREQUISITE_TEXT = 'The gap analysis report must be approved by the Company SPOC before it can be pushed to the dashboard.';
export const PUSH_SCORES_NOT_VALIDATED_TEXT = 'Review and validate the gap analysis scores before pushing them to the dashboard.';
export const PUSH_TECH_SCORES_NOT_VALIDATED_TEXT = 'The technical evaluation scores must be approved by a human reviewer before they can be pushed to the dashboard.';
export const TECH_ASSESSMENT_REQUIRED_TEXT = 'The technical assessment must be completed and its scores validated, or the Advisor must record that it is not applicable (with a reason), before the gap analysis can go on.';
export const SEND_BACK_REASON_REQUIRED_TEXT = 'Sending the gap analysis back replaces the validated technical scores or an already pushed dashboard snapshot. Say why, so the history records it.';
export const OVERRIDE_NOTE_MIN_LENGTH = 10;
export const TECH_WAIVER_MIN_REASON_LENGTH = 20;
export const PUSH_NO_SCORED_ITEMS_TEXT = 'There are no scored gap analysis items to push yet, so nothing is sent to the dashboard rather than showing a 0% readiness.';
/** The push 409 reasons (server-side gate); each also forces a profile reload because the stored state is what refused it. */
export const GAP_PUSH_REFUSAL_REASONS = ['PUSH_PREREQUISITE_NOT_MET', 'PUSH_SCORES_NOT_VALIDATED', 'PUSH_TECH_SCORES_NOT_VALIDATED', 'PUSH_NO_SCORED_ITEMS', 'TECH_ASSESSMENT_REQUIRED'];

/**
 * Why "Push to Dashboard" is unavailable, or '' when allowed. Mirrors the server's checks
 * (services/gapDashboardSnapshot.ts, same order); the server stays the authority and also refuses
 * a push with no scored items (PUSH_NO_SCORED_ITEMS), which only it can know.
 */
export function gapPushBlockedReason({ status, scoresValidated, techEvaluated, techScoresValidated, techAssessmentDone, techWaived }) {
    if (!GAP_PUSH_ALLOWED_STATUSES.includes(status)) return PUSH_PREREQUISITE_TEXT;
    if (scoresValidated !== true) return PUSH_SCORES_NOT_VALIDATED_TEXT;
    if (techEvaluated && techScoresValidated !== true && !techWaived) return PUSH_TECH_SCORES_NOT_VALIDATED_TEXT;
    if (!techAssessmentPrerequisiteMet({ techAssessmentDone, techScoresValidated, waived: techWaived })) return TECH_ASSESSMENT_REQUIRED_TEXT;
    return '';
}

/**
 * Send for review, share with the Lead Advisor and push all need the technical chain: a completed AND validated
 * technical assessment, OR the Advisor's audited "not applicable" waiver. Mirrors the API (which refuses with
 * 409 TECH_ASSESSMENT_REQUIRED); the server stays the authority.
 */
export function techAssessmentPrerequisiteMet({ techAssessmentDone, techScoresValidated, waived }) {
    return waived === true || (techAssessmentDone === true && techScoresValidated === true);
}

/**
 * Technical rows whose score the Advisor changed (or filled by hand because the AI failed) and for which no
 * reason of at least OVERRIDE_NOTE_MIN_LENGTH characters was given. The API refuses these (400 OVERRIDE_NOTE_REQUIRED).
 * @returns {string[]} row ids
 */
export function techRowIsOverridden(o, e) {
    if (!o || !e || typeof e !== 'object') return false;
    const r = Number.isFinite(e.readinessScore) ? e.readinessScore : undefined;
    const x = Number.isFinite(e.expectedScore) ? e.expectedScore : undefined;
    if (r === undefined && x === undefined) return false;
    return o.aiFailed === true || (r !== undefined && r !== o.readinessScore) || (x !== undefined && x !== o.expectedScore);
}

export function techOverrideRowsMissingNote({ observations, overrides }) {
    const ov = overrides && typeof overrides === 'object' ? overrides : {};
    const missing = [];
    for (const o of Array.isArray(observations) ? observations : []) {
        const e = ov[o.id];
        if (!techRowIsOverridden(o, e)) continue;
        const note = typeof e.overrideNote === 'string' ? e.overrideNote.trim() : '';
        if (note.length < OVERRIDE_NOTE_MIN_LENGTH) missing.push(String(o.id));
    }
    return missing;
}

/** 409 `error.details.reason` values -> plain sentence. Unknown reasons fall back to the server's own text. */
export function gapEvidenceReasonText(reason, details) {
    switch (reason) {
        case 'STAGE_PREREQUISITE_NOT_MET':
            return 'Tool-test evidence is locked until Stage 3 (Project onboarding) has been approved by the Company SPOC.';
        case 'GAP_ANALYSIS_LOCKED':
            return 'The gap analysis has been sent on for review or approved, so tool-test evidence can no longer change. It reopens only if the Company SPOC sends the gap analysis back to Draft.';
        case 'DRAFT_CAP_REACHED': {
            const cap = num(details?.cap);
            const active = num(details?.activeDrafts);
            return `This project already has ${active ?? 'the maximum number of'} unresolved AI drafts${cap != null ? ` (limit ${cap})` : ''}. Approve or reject some before generating more.`;
        }
        case 'ALREADY_REJECTED':
            return 'This summary has already been rejected. A rejected draft cannot be approved; generate a new summary instead.';
        case 'ALREADY_APPROVED':
            return 'This summary has already been approved, so it cannot be changed or rejected.';
        case 'CONCURRENT_RESOLUTION':
            return 'Someone else resolved this summary at the same moment. Reload to see its current state.';
        case 'UNRESOLVED_DRAFT':
            return 'This recorded tool has an unresolved AI draft. Approve or reject it first.';
        case 'ALREADY_MARKED':
            return 'This recorded tool is already marked as having no output.';
        case 'ASSESSMENT_COMPLETE':
            return ASSESSMENT_COMPLETE_TEXT;
        case 'NOT_APPROVED':
            return 'Only an approved summary can be linked to a recorded tool.';
        case 'CHAIN_BROKEN':
            return 'The stored summary no longer matches its approval hash chain, so it cannot be linked as evidence. Report this to your platform administrator.';
        case 'ALREADY_LINKED':
            return 'This summary is already linked to a recorded tool. A summary can be linked only once.';
        case 'TOOL_MARKED_NO_OUTPUT':
            return 'That recorded tool is marked as having no output, so no summary can be uploaded for it, approved or linked to it.';
        case 'CONCURRENT_CHANGE':
            return 'Someone changed this summary at the same moment. Reload to see its current state, then try again.';
        case 'TECH_ASSESSMENT_NOT_COMPLETE':
            return TECH_EVAL_NEEDS_COMPLETE_TEXT;
        case 'STALE_EVALUATION':
            return STALE_EVALUATION_TEXT;
        case 'EVIDENCE_CHANGED':
            return EVIDENCE_CHANGED_TEXT;
        case 'REOPEN_BLOCKED_SHARED_WITH_LA':
            return REOPEN_BLOCKED_SHARED_TEXT;
        case 'REOPEN_REASON_REQUIRED':
            return REOPEN_REASON_REQUIRED_TEXT;
        case 'PUSH_PREREQUISITE_NOT_MET':
            return PUSH_PREREQUISITE_TEXT;
        case 'PUSH_SCORES_NOT_VALIDATED':
            return PUSH_SCORES_NOT_VALIDATED_TEXT;
        case 'PUSH_TECH_SCORES_NOT_VALIDATED':
            return PUSH_TECH_SCORES_NOT_VALIDATED_TEXT;
        case 'PUSH_NO_SCORED_ITEMS':
            return PUSH_NO_SCORED_ITEMS_TEXT;
        case 'TECH_ASSESSMENT_REQUIRED':
            return TECH_ASSESSMENT_REQUIRED_TEXT;
        case 'SEND_BACK_REASON_REQUIRED':
            return SEND_BACK_REASON_REQUIRED_TEXT;
        case 'ALREADY_WAIVED':
            return 'The technical assessment is already marked not applicable. Restore it first if you need to change the reason.';
        case 'NOT_WAIVED':
            return 'There is no technical-assessment waiver to restore.';
        case 'WAIVER_REASON_REQUIRED':
            return `Give a reason of at least ${TECH_WAIVER_MIN_REASON_LENGTH} characters to mark the technical assessment not applicable.`;
        case 'NO_SUCH_TOOL_RECORD':
            return 'That recorded tool no longer exists. Reload and pick another.';
        case 'ALREADY_EVIDENCED':
            return 'This recorded tool already has an approved tool-test summary, so it cannot be marked as having no output.';
        default:
            return null;
    }
}

/** Text for any thrown/received error on an evidence call or a refused profile save. */
export function gapEvidenceErrorText(err, fallback) {
    if (err instanceof ApiError && (err.status === 409 || (err.status === 404 && err.details?.reason === 'NO_SUCH_TOOL_RECORD'))) {
        const t = gapEvidenceReasonText(err.details?.reason, err.details);
        if (t) return t;
    }
    return userMessageForError(err, fallback);
}

/** Text for a refused profile PUT: the wizard-level 409s read differently from the per-summary ones. */
export function gapProfileSaveErrorText(err, fallback) {
    if (err instanceof ApiError && err.status === 409 && err.details?.reason === 'CONCURRENT_CHANGE') {
        return 'The assessment changed at the same moment. The latest version has been reloaded; check it, then try again.';
    }
    return gapEvidenceErrorText(err, fallback);
}

const HISTORY_KIND_LABELS = {
    SPOC_SEND_BACK: 'Company SPOC sent the gap analysis back',
    WITHDRAWN_FROM_REVIEW: 'Gap analysis withdrawn from review',
    ADVISOR_REOPEN: 'Advisor reopened the assessment',
};

/** Server-written, append-only `techEvaluationHistory[]` -> read-only rows, newest first. Never mutates or re-derives hashes. */
export function techEvaluationHistoryRows(history) {
    if (!Array.isArray(history)) return [];
    return history
        .filter((e) => e && typeof e === 'object')
        .map((e, i) => ({
            key: e.id || `${e.snapshotSha256 || 'entry'}-${i}`,
            kind: e.kind || null,
            label: HISTORY_KIND_LABELS[e.kind] || (e.kind ? String(e.kind).replace(/_/g, ' ').toLowerCase() : 'Superseded'),
            reason: e.reason || '',
            humanReason: typeof e.humanReason === 'string' ? e.humanReason : '',
            supersededAt: e.supersededAt || null,
            supersededBy: e.supersededBy?.email || e.supersededBy?.userId || null,
            observationCount: Array.isArray(e.snapshot?.techObservations) ? e.snapshot.techObservations.length : null,
            scoresValidated: e.snapshot?.techScoresValidated === true,
            sha256: e.snapshotSha256 || null,
        }))
        .reverse();
}

/**
 * Completion rule: >= 1 approved summary, zero unresolved drafts, and every recorded tool either
 * linked to an approved summary or marked no-output. Returns the exact list of things blocking it.
 *
 * @returns {{ blockers: string[], toolStates: Array<{ id, toolName, state: 'APPROVED'|'DRAFT'|'NO_OUTPUT'|'NEEDS_EVIDENCE', mark?: object }> }}
 */
export function evaluateToolEvidenceCompletion({ toolRecords, marks, approved, drafts, counts, loadStatus, loadError }) {
    const blockers = [];
    const records = Array.isArray(toolRecords) ? toolRecords : [];
    const noOutput = new Map((Array.isArray(marks) ? marks : []).filter((m) => m && m.toolRecordId).map((m) => [m.toolRecordId, m]));
    const approvedByTool = new Set(approved.map((a) => a.toolRecordId).filter(Boolean));
    const draftByTool = new Set(drafts.map((d) => d.toolRecordId).filter(Boolean));

    const toolStates = records.map((r) => {
        let state = 'NEEDS_EVIDENCE';
        if (approvedByTool.has(r.id)) state = 'APPROVED';
        else if (draftByTool.has(r.id)) state = 'DRAFT';
        else if (noOutput.has(r.id)) state = 'NO_OUTPUT';
        return { id: r.id, toolName: r.toolName, state, mark: noOutput.get(r.id) };
    });
    // Legacy approved summaries predate the upload-time link: they evidence no tool until linked.
    const legacyUnlinked = approved.filter((a) => !a.toolRecordId);

    if (loadStatus === 'error' || loadStatus === 'denied') {
        blockers.push(`Approved summaries could not be confirmed (${loadError || 'load failed'}).`);
        return { blockers, toolStates };
    }
    if (loadStatus !== 'ready') {
        blockers.push('Tool-test summaries are still loading.');
        return { blockers, toolStates };
    }
    const wantApproved = num(counts?.APPROVED);
    const wantDrafts = num(counts?.AI_DRAFT);
    const unloaded = Math.max(0, (wantApproved ?? approved.length) - approved.length) + Math.max(0, (wantDrafts ?? drafts.length) - drafts.length);
    if (unloaded > 0) {
        blockers.push(`${unloaded} more summar${unloaded === 1 ? 'y has' : 'ies have'} not been loaded yet. Use "Load more" so every draft can be checked.`);
    }
    if (approved.length === 0) blockers.push('No approved tool-test summary yet. At least one is required.');
    for (const d of drafts) {
        // Name the draft by what the Advisor recognises (tool, file, upload time), never by an id fragment.
        const tool = d.toolRecordId ? records.find((r) => r.id === d.toolRecordId)?.toolName : null;
        const subject = tool ? `for ${tool}` : `for a ${d.toolType || 'tool'} summary not linked to a recorded tool`;
        const detail = [d.sourceFileName, d.generatedAt ? `uploaded ${formatDdMmYyyyTime(d.generatedAt)}` : null].filter(Boolean).join(', ');
        blockers.push(`Unresolved AI draft ${subject}${detail ? ` (${detail})` : ''}: approve or reject it.`);
    }
    for (const t of toolStates) {
        if (t.state === 'NEEDS_EVIDENCE') {
            blockers.push(`Recorded tool "${t.toolName}" has no approved summary linked to it and is not marked as having no output.${legacyUnlinked.length > 0 ? ` ${legacyUnlinked.length} approved summar${legacyUnlinked.length === 1 ? 'y is' : 'ies are'} not linked to any recorded tool, so they do not count for this tool. Use "Link to a recorded tool" on one.` : ''}`);
        }
    }
    return { blockers, toolStates, legacyUnlinkedCount: legacyUnlinked.length };
}

/** Recorded tools a legacy approved summary may be linked to: no approved/linked summary yet, and not marked no-output. */
export function linkableToolRecords({ toolRecords, marks, approved }) {
    const noOutput = new Set((Array.isArray(marks) ? marks : []).map((m) => m?.toolRecordId).filter(Boolean));
    const evidenced = new Set((Array.isArray(approved) ? approved : []).map((a) => a.toolRecordId).filter(Boolean));
    return (Array.isArray(toolRecords) ? toolRecords : []).filter((r) => !evidenced.has(r.id) && !noOutput.has(r.id));
}
